import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  getDoc,
  setDoc,
  query,
  orderBy,
  where
} from 'firebase/firestore';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut
} from 'firebase/auth';
import { db, auth, storage } from '../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

// Products CRUD operations
export const addProduct = async (productData) => {
  try {
    const docRef = await addDoc(collection(db, 'products'), {
      ...productData,
      createdAt: new Date(),
      updatedAt: new Date()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error adding product:', error);
    return { success: false, error: error.message };
  }
};

export const updateProduct = async (productId, productData) => {
  try {
    const productRef = doc(db, 'products', productId);
    await updateDoc(productRef, {
      ...productData,
      updatedAt: new Date()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating product:', error);
    return { success: false, error: error.message };
  }
};

export const deleteProduct = async (productId) => {
  try {
    await deleteDoc(doc(db, 'products', productId));
    return { success: true };
  } catch (error) {
    console.error('Error deleting product:', error);
    return { success: false, error: error.message };
  }
};

export const getProducts = async () => {
  try {
    const q = query(collection(db, 'products'), orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    const products = [];
    querySnapshot.forEach((doc) => {
      products.push({
        id: doc.id,
        ...doc.data()
      });
    });
    return { success: true, products };
  } catch (error) {
    console.error('Error getting products:', error);
    return { success: false, error: error.message };
  }
};

// Upload multiple images to Firebase Storage and return their download URLs
export const uploadProductImages = async (files, productId = null) => {
  if (!files || files.length === 0) return [];
  const urls = [];
  const uploadPromises = Array.from(files).slice(0, 5).map(async (file, idx) => {
    // Use productId if available, otherwise use timestamp
    const filePath = `products/${productId || Date.now()}_${idx}_${file.name}`;
    const storageRef = ref(storage, filePath);
    await uploadBytes(storageRef, file);
    const url = await getDownloadURL(storageRef);
    urls.push(url);
  });
  await Promise.all(uploadPromises);
  return urls;
};

// Upload a single introduction image to Firebase Storage and return its download URL
export const uploadIntroImage = async (file, productId = null) => {
  if (!file) return '';
  const filePath = `products/${productId || Date.now()}_intro_${file.name}`;
  const storageRef = ref(storage, filePath);
  await uploadBytes(storageRef, file);
  const url = await getDownloadURL(storageRef);
  return url;
};

// Upload payment receipt to Firebase Storage with custom filename format
export const uploadPaymentReceipt = async (file, orderId) => {
  if (!file) return '';
  
  // Generate filename: receipt_{year}{month}{6 digits increment}
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const randomNum = String(Math.floor(Math.random() * 1000000)).padStart(6, '0');
  const fileName = `receipt_${year}${month}${randomNum}`;
  const fileExtension = file.name.split('.').pop();
  
  const filePath = `receipts/${fileName}.${fileExtension}`;
  const storageRef = ref(storage, filePath);
  await uploadBytes(storageRef, file);
  const url = await getDownloadURL(storageRef);
  return url;
};

// Upload testimonial image to Firebase Storage
export const uploadTestimonialImage = async (file, testimonialId = null) => {
  if (!file) return '';
  const filePath = `testimonials/${testimonialId || Date.now()}_${file.name}`;
  const storageRef = ref(storage, filePath);
  await uploadBytes(storageRef, file);
  const url = await getDownloadURL(storageRef);
  return url;
};

// Upload brand logo to Firebase Storage
export const uploadBrandLogo = async (file, brandId = null) => {
  if (!file) return '';
  const filePath = `brands/${brandId || Date.now()}_${file.name}`;
  const storageRef = ref(storage, filePath);
  await uploadBytes(storageRef, file);
  const url = await getDownloadURL(storageRef);
  return url;
};

// Authentication
export const loginUser = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return { success: true, user: userCredential.user };
  } catch (error) {
    console.error('Error logging in:', error);
    return { success: false, error: error.message };
  }
};

export const registerUser = async (email, password) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    return { success: true, user: userCredential.user };
  } catch (error) {
    console.error('Error registering user:', error);
    return { success: false, error: error.message };
  }
};

export const logoutUser = async () => {
  try {
    await signOut(auth);
    return { success: true };
  } catch (error) {
    console.error('Error logging out:', error);
    return { success: false, error: error.message };
  }
};

// Get current user
export const getCurrentUser = () => {
  return auth.currentUser;
};

// Auth state listener
export const onAuthStateChanged = (callback) => {
  return auth.onAuthStateChanged(callback);
};

// Orders CRUD operations
export const addOrder = async (orderData) => {
  try {
    const docRef = await addDoc(collection(db, 'orders'), {
      ...orderData,
      createdAt: new Date(),
      updatedAt: new Date()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error adding order:', error);
    return { success: false, error: error.message };
  }
};

export const updateOrder = async (orderId, orderData) => {
  try {
    const orderRef = doc(db, 'orders', orderId);
    await updateDoc(orderRef, {
      ...orderData,
      updatedAt: new Date()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating order:', error);
    return { success: false, error: error.message };
  }
};

export const deleteOrder = async (orderId) => {
  try {
    await deleteDoc(doc(db, 'orders', orderId));
    return { success: true };
  } catch (error) {
    console.error('Error deleting order:', error);
    return { success: false, error: error.message };
  }
};

export const getOrders = async () => {
  try {
    const q = query(collection(db, 'orders'), orderBy('dateTime', 'desc'));
    const querySnapshot = await getDocs(q);
    const orders = [];
    querySnapshot.forEach((doc) => {
      orders.push({
        id: doc.id,
        ...doc.data()
      });
    });
    return { success: true, orders };
  } catch (error) {
    console.error('Error getting orders:', error);
    return { success: false, error: error.message };
  }
};

export const getOrderById = async (orderId) => {
  try {
    const orderRef = doc(db, 'orders', orderId);
    const orderSnap = await getDoc(orderRef);
    if (orderSnap.exists()) {
      return { success: true, order: { id: orderSnap.id, ...orderSnap.data() } };
    } else {
      return { success: false, error: 'Order not found' };
    }
  } catch (error) {
    console.error('Error getting order:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get orders from the 'orders' collection by phone number
 * Searches for orders where purchaserPhoneNumber matches the provided phone number
 * @param {string} phoneNumber - Phone number to search for
 * @returns {Promise<{success: boolean, orders?: Array, error?: string}>}
 */
export const getOrdersByPhoneNumber = async (phoneNumber) => {
  try {
    if (!phoneNumber || !phoneNumber.trim()) {
      return { success: false, error: 'Phone number is required' };
    }

    // Normalize phone number: remove spaces, dashes, parentheses
    const normalizedPhone = phoneNumber.trim().replace(/[\s\-()]/g, '');
    
    // Query the 'orders' collection using the exact phone number as stored
    // First try with the provided phone number
    let q = query(
      collection(db, 'orders'),
      where('purchaserPhoneNumber', '==', phoneNumber.trim()),
      orderBy('dateTime', 'desc')
    );
    
    let querySnapshot;
    try {
      querySnapshot = await getDocs(q);
    } catch (indexError) {
      // If index doesn't exist, try without orderBy
      q = query(
        collection(db, 'orders'),
        where('purchaserPhoneNumber', '==', phoneNumber.trim())
      );
      querySnapshot = await getDocs(q);
    }
    
    const orders = [];
    querySnapshot.forEach((doc) => {
      orders.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    // If no results and phone number was normalized differently, try normalized version
    if (orders.length === 0 && normalizedPhone !== phoneNumber.trim()) {
      try {
        q = query(
          collection(db, 'orders'),
          where('purchaserPhoneNumber', '==', normalizedPhone),
          orderBy('dateTime', 'desc')
        );
        querySnapshot = await getDocs(q);
      } catch (indexError2) {
        // If index doesn't exist, try without orderBy
        q = query(
          collection(db, 'orders'),
          where('purchaserPhoneNumber', '==', normalizedPhone)
        );
        querySnapshot = await getDocs(q);
      }
      
      querySnapshot.forEach((doc) => {
        orders.push({
          id: doc.id,
          ...doc.data()
        });
      });
    }
    
    // Sort manually if we got results without orderBy
    if (orders.length > 0) {
      orders.sort((a, b) => {
        const dateA = a.dateTime?.toDate ? a.dateTime.toDate() : new Date(a.dateTime || 0);
        const dateB = b.dateTime?.toDate ? b.dateTime.toDate() : new Date(b.dateTime || 0);
        return dateB - dateA; // Descending order (newest first)
      });
    }
    
    return { success: true, orders };
  } catch (error) {
    console.error('Error getting orders by phone number:', error);
    return { success: false, error: error.message };
  }
};

// Testimonials CRUD operations
export const getTestimonials = async () => {
  try {
    const q = query(collection(db, 'testimonials'), orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    const testimonials = [];
    querySnapshot.forEach((doc) => {
      testimonials.push({
        id: doc.id,
        ...doc.data()
      });
    });
    return { success: true, testimonials };
  } catch (error) {
    console.error('Error getting testimonials:', error);
    return { success: false, error: error.message };
  }
};

export const addTestimonial = async (testimonialData) => {
  try {
    const docRef = await addDoc(collection(db, 'testimonials'), {
      ...testimonialData,
      createdAt: new Date(),
      updatedAt: new Date()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error adding testimonial:', error);
    return { success: false, error: error.message };
  }
};

export const updateTestimonial = async (testimonialId, testimonialData) => {
  try {
    const testimonialRef = doc(db, 'testimonials', testimonialId);
    await updateDoc(testimonialRef, {
      ...testimonialData,
      updatedAt: new Date()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating testimonial:', error);
    return { success: false, error: error.message };
  }
};

export const deleteTestimonial = async (testimonialId) => {
  try {
    await deleteDoc(doc(db, 'testimonials', testimonialId));
    return { success: true };
  } catch (error) {
    console.error('Error deleting testimonial:', error);
    return { success: false, error: error.message };
  }
}; 

// Brands CRUD operations
export const getBrands = async () => {
  try {
    const q = query(collection(db, 'brands'), orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    const brands = [];
    querySnapshot.forEach((doc) => {
      brands.push({
        id: doc.id,
        ...doc.data()
      });
    });
    return { success: true, brands };
  } catch (error) {
    console.error('Error getting brands:', error);
    return { success: false, error: error.message };
  }
};

export const addBrand = async (brandData) => {
  try {
    const docRef = await addDoc(collection(db, 'brands'), {
      ...brandData,
      createdAt: new Date(),
      updatedAt: new Date()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error adding brand:', error);
    return { success: false, error: error.message };
  }
};

export const updateBrand = async (brandId, brandData) => {
  try {
    const brandRef = doc(db, 'brands', brandId);
    await updateDoc(brandRef, {
      ...brandData,
      updatedAt: new Date()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating brand:', error);
    return { success: false, error: error.message };
  }
};

export const deleteBrand = async (brandId) => {
  try {
    await deleteDoc(doc(db, 'brands', brandId));
    return { success: true };
  } catch (error) {
    console.error('Error deleting brand:', error);
    return { success: false, error: error.message };
  }
};

// Shipping fees configuration
export const DEFAULT_SHIPPING_FEES = {
  westMalaysia: {
    firstKg: 10,
    additionalKg: 3
  },
  eastMalaysia: {
    firstKg: 10,
    additionalKg: 12
  }
};

export const getShippingFees = async () => {
  try {
    const shippingFeesRef = doc(db, 'settings', 'shippingFees');
    const shippingFeesSnap = await getDoc(shippingFeesRef);

    if (!shippingFeesSnap.exists()) {
      // Create defaults if not initialized yet
      await setDoc(shippingFeesRef, {
        ...DEFAULT_SHIPPING_FEES,
        updatedAt: new Date()
      });
      return { success: true, shippingFees: DEFAULT_SHIPPING_FEES };
    }

    const data = shippingFeesSnap.data() || {};
    return {
      success: true,
      shippingFees: {
        westMalaysia: {
          firstKg: Number(data.westMalaysia?.firstKg ?? DEFAULT_SHIPPING_FEES.westMalaysia.firstKg),
          additionalKg: Number(data.westMalaysia?.additionalKg ?? DEFAULT_SHIPPING_FEES.westMalaysia.additionalKg)
        },
        eastMalaysia: {
          firstKg: Number(data.eastMalaysia?.firstKg ?? DEFAULT_SHIPPING_FEES.eastMalaysia.firstKg),
          additionalKg: Number(data.eastMalaysia?.additionalKg ?? DEFAULT_SHIPPING_FEES.eastMalaysia.additionalKg)
        }
      }
    };
  } catch (error) {
    console.error('Error getting shipping fees:', error);
    return { success: false, error: error.message };
  }
};

export const updateShippingFees = async (shippingFees) => {
  try {
    const shippingFeesRef = doc(db, 'settings', 'shippingFees');
    await setDoc(shippingFeesRef, {
      westMalaysia: {
        firstKg: Number(shippingFees.westMalaysia.firstKg),
        additionalKg: Number(shippingFees.westMalaysia.additionalKg)
      },
      eastMalaysia: {
        firstKg: Number(shippingFees.eastMalaysia.firstKg),
        additionalKg: Number(shippingFees.eastMalaysia.additionalKg)
      },
      updatedAt: new Date()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating shipping fees:', error);
    return { success: false, error: error.message };
  }
};